/**
 * 工具函数统一导出入口
 * 提供所有工具函数的集中访问
 *
 * @example
 * // 按需导入
 * import { safeTrim, checkSoftwareSuffix } from '@/utils/formUtils'
 *
 * // 导入全部
 * import * as formUtils from '@/utils/formUtils'
 */

// ==================== 表单校验相关 ====================
export {
  safeTrim,
  startsWithNonAlphanumericSymbol,
  hasBrackets,
  checkSoftwareSuffix,
  hasAppKeywords,
  createSoftwareNameValidator,
  createBrandValidator,
  createShortNameValidator,
  createVersionValidator,
  createApplicantValidator,
  requiredRules
} from '../formValidators';

// ==================== 表单校验规则创建器 ====================
export {
  createSoftwareNameRules,
  createBrandNameRules,
  createShortNameRules,
  createVersionRules,
  createApplicantRules,
  validateSoftwareBasicInfo,
  validateApplicantsInfo
} from '../formRuleCreators';

// ==================== 字符串处理相关 ====================
export {
  splitApplicant,
  generateRandomString,
  generateRandomNumber,
  generateId,
  trimStringValues,
  copyToClipboard,
  setDisplayName
} from '../stringUtils';

// ==================== 定时器管理相关 ====================
export {
  createTimerManager,
  createProgressManager,
  debounceWithCancel,
  throttle
} from '../timerUtils';

// ==================== DOM操作相关 ====================
export {
  scrollToElement,
  openWindow,
  getHighlightSegments,
  setDriverPopoverPosition,
  smoothScrollToTop,
  isElementInViewport,
  getElementOffset
} from '../domUtils';

// ==================== 数据转换相关 ====================
export {
  configApplicants,
  configSubmitParams,
  configNewOutlineJson,
  determineDevelopMode,
  createApplicantObject,
  parseApplicantsFromServer,
  configAutoCheckData,
  checkHasPersonalApplicant
} from '../dataTransform';

// ==================== 证件号码验证相关 ====================
export {
  validateIDCard,
  validateCreditCode,
  validateIdByType,
  validatePhone,
  validateEmail,
  validateLength,
  hasSpecialChar,
  validateUrl,
  createLengthValidator,
  createRequiredValidator
} from '../validationUtils';

// ==================== API调用相关 ====================
export {
  createApiWrapper,
  batchApiCall,
  retryApiCall,
  validateFileBeforeUpload,
  handleUploadSuccess,
  safeJsonParse,
  safeJsonStringify,
  preventDuplicateSubmit,
  pollUntil,
  downloadFile,
  batchDownloadFiles
} from '../apiUtils';

// ==================== 日期处理相关 ====================
export {
  createDisabledDateFunc,
  createCompleteDateDisabledFunc,
  createFirstAppearDateDisabledFunc,
  checkDateReasonable,
  formatDate,
  getDaysDiff,
  addDays,
  calculateBirthdayAfterYears,
  isLeapYear,
  getDaysInMonth,
  isValidDate,
  getToday,
  isSameDay
} from '../dateUtils';

// ==================== 著作权人管理相关 ====================
export {
  setDevelopModeByApplicants,
  validateApplicantInfo,
  validateApplicantList,
  setOwnDisplayName,
  setAgtDisplayName,
  configCopyrightOwners,
  parseApplicantString,
  fillApplicantInfo,
  needAuthorization,
  isAccountError,
  getAccountErrors,
  moveApplicantUp,
  moveApplicantDown
} from '../applicantUtils';

// ==================== 表单业务逻辑相关 ====================
export {
  getApplicantAddressInfo,
  checkAuth,
  setOwnDisplayName as setOwnDisplayNameBiz,
  setAgtDisplayName as setAgtDisplayNameBiz,
  validateApplicantInput,
  validateApplicantsList,
  moveArrayItemUp,
  moveArrayItemDown,
  isHMTArea,
  formatAreaString
} from '../formBusinessUtils';

// ==================== 文件上传相关 ====================
export {
  beforeUploadCooperateUrl,
  beforeUploadOtherFile,
  beforeUpload,
  handleFileUploadSuccess,
  handleFileUploadError,
  deleteFileFromList,
  clearFileObject,
  formatFileSize,
  isImageFile,
  isPdfFile,
  getFileExtension,
  getPreviewUrl
} from '../fileUploadUtils';

// ==================== 常量定义 ====================
/**
 * 软件类型枚举
 */
export const SOFTWARE_TYPE = {
  PC: 0,
  ANDROID: 1,
  IOS: 2,
  MINI_PROGRAM: 3,
  EMBEDDED: 4
};

/**
 * 申请人类型枚举
 */
export const APPLICANT_TYPE = {
  OWN: 'OWN',      // 我是申请人
  AGT: 'AGT'       // 我是代理人
};

/**
 * 开发方式枚举
 */
export const DEVELOP_MODE = {
  SINGLE: 'SINGLE',       // 单独开发
  COOPERATE: 'COOPERATE', // 合作开发
  COMMISSION: 'COMMISSION', // 委托开发
  TASK: 'TASK'            // 下达任务开发
};

/**
 * 著作权人类型枚举
 */
export const COPYRIGHT_OWNER_KIND = {
  SINGLE_PEOPLE: 'SINGLE_PEOPLE',         // 个人
  SINGLE_ENTERPRISE: 'SINGLE_ENTERPRISE'  // 企业
};

/**
 * APP关键词检测结果枚举
 */
export const APP_KEYWORD_TYPE = {
  NONE: 0,        // 无匹配
  ANDROID: 1,     // 安卓相关
  IOS: 2,         // 苹果相关
  MINI_APP: 3,    // 小程序相关
  APP: 21         // APP关键词
};

/**
 * 默认版本号
 */
export const DEFAULT_VERSION = 'V1.0';

/**
 * 滚动偏移量（用于表单错误定位）
 */
export const SCROLL_OFFSET = 144;

/**
 * 延迟执行时间（毫秒）
 */
export const DELAY_TIME = {
  SHORT: 100,
  MEDIUM: 500,
  LONG: 1000
};
